import { DateTime } from 'luxon';
import { ComponentType, useCallback, useEffect, useRef, useState } from 'react';

import styles from './Countdown.module.scss';

export interface ICounterSinglePart {
  value: string | number;
  label: string;
  valueClasses: string[];
  labelsClasses: string[];
  containerClasses: string[];
  valueWrapperClasses?: string[];
}

const DefaultCountdownPart = ({
  value,
  label,
  valueClasses,
  labelsClasses,
  containerClasses,
  valueWrapperClasses = [],
}: ICounterSinglePart) => {
  return (
    <div className={containerClasses.join(' ')}>
      <div className={valueWrapperClasses.join(' ')}>
        <div className={valueClasses.join(' ')}>{value || 0}</div>
      </div>
      <div className={labelsClasses.join(' ')}>{label}</div>
    </div>
  );
};

interface CountdownProps {
  endTimestamp: number;
  days?: boolean;
  label?: string;
  counterClass?: string[];
  labelClass?: string[];
  valueClass?: string[];
  labelsClass?: string[];
  containerClass?: string[];
  valueWrapperClass?: string[];
  finished?: () => void;
  /** If true, shows only total days without months/years breakdown */
  maxDaysOnly?: boolean;
  CountdownPartComponent?: ComponentType<ICounterSinglePart>;
}

// Pure utility function moved outside component
const padZero = (value: number) => value.toString().padStart(2, '0');

const Countdown = ({
  endTimestamp,
  days = true,
  label,
  counterClass,
  labelClass,
  valueClass,
  valueWrapperClass,
  labelsClass,
  containerClass,
  finished = () => {},
  maxDaysOnly = false,
  CountdownPartComponent = DefaultCountdownPart,
}: CountdownProps) => {
  const counterClasses = [...(counterClass ?? []), styles['countdown__counter']];
  const labelClasses = [...(labelClass ?? []), styles['countdown__label']];
  const valueClasses = [...(valueClass ?? []), styles['countdown-part-container__value']];
  const labelsClasses = [...(labelsClass ?? []), styles['countdown-part-container__label']];
  const containerClasses = [...(containerClass ?? []), styles['countdown-part-container']];

  const finishedRef = useRef(finished);
  const hasFinishedRef = useRef(false);

  // Keep the finished callback reference up to date
  useEffect(() => {
    finishedRef.current = finished;
  }, [finished]);

  const getDateParts = useCallback(
    (endTimestamp: number, days: boolean) => {
      const expirationDate = DateTime.fromMillis(endTimestamp);
      const duration = expirationDate.diffNow(['years', 'months', 'days', 'hours', 'minutes', 'seconds']);

      const leftTimeParts = [
        {
          key: 'hours',
          value: !days ? padZero(Math.floor(duration.hours)) : Math.floor(duration.hours),
        },
        {
          key: 'minutes',
          value: !days ? padZero(Math.floor(duration.minutes)) : Math.floor(duration.minutes),
        },
        {
          key: 'seconds',
          value: !days ? padZero(Math.floor(duration.seconds)) : Math.floor(duration.seconds),
        },
      ];

      if (days) {
        if (maxDaysOnly) {
          // Calculate total days instead of breaking down into years/months/days
          const totalDays = Math.floor(expirationDate.diff(DateTime.now(), 'days').days);
          leftTimeParts.unshift({
            key: 'days',
            value: totalDays,
          });
        } else {
          // Normal behavior: show days, then add months/years if present
          leftTimeParts.unshift({
            key: 'days',
            value: Math.floor(duration.days),
          });

          if (duration.months) {
            leftTimeParts.unshift({
              key: 'months',
              value: Math.floor(duration.months),
            });
          }

          if (duration.years) {
            leftTimeParts.unshift({
              key: 'years',
              value: Math.floor(duration.years),
            });
          }
        }
      }

      return leftTimeParts;
    },
    [maxDaysOnly]
  );

  const [leftTime, setLeftTime] = useState(() => getDateParts(endTimestamp, days));
  const intervalId = useRef<NodeJS.Timer | null>(null);

  const stopTimer = useCallback(() => {
    if (intervalId?.current !== null) {
      clearInterval(intervalId.current);
      intervalId.current = null;
    }

    // Call finished callback only once and not during cleanup
    if (!hasFinishedRef.current) {
      hasFinishedRef.current = true;
      finishedRef.current();
    }
  }, []);

  const startTimer = useCallback(() => {
    // Clear any existing timer
    if (intervalId.current !== null) {
      clearInterval(intervalId.current);
    }

    // Reset finished flag when starting a new timer
    hasFinishedRef.current = false;

    intervalId.current = setInterval(() => {
      const leftTimeParts = getDateParts(endTimestamp, days);
      const timeEnded = leftTimeParts.every((timeParts) => Number(timeParts.value) <= 0);

      setLeftTime(leftTimeParts);

      if (timeEnded) {
        stopTimer();
      }
    }, 1000);
  }, [endTimestamp, days, getDateParts, stopTimer]);

  useEffect(() => {
    startTimer();

    return () => {
      // Cleanup: stop timer without calling finished callback
      if (intervalId?.current !== null) {
        clearInterval(intervalId.current);
        intervalId.current = null;
      }
    };
  }, [startTimer]);
  return (
    <div className={styles['countdown']}>
      <div className={labelClasses.join(' ')}>{label}</div>
      <div className={counterClasses.join(' ')}>
        {leftTime.map((timeParts) => (
          <CountdownPartComponent
            key={timeParts.key}
            label={timeParts.key}
            value={Number(timeParts.value) < 0 ? 0 : timeParts.value}
            valueClasses={valueClasses}
            labelsClasses={labelsClasses}
            containerClasses={containerClasses}
            valueWrapperClasses={[...(valueWrapperClass ?? [])]}
          />
        ))}
      </div>
    </div>
  );
};

export default Countdown;
